'use strict';

import React, { useState, useEffect, useCallback, useContext, useRef, forwardRef, useImperativeHandle, Component, useMemo } from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import moment from 'moment';
import platform from 'platform';
import PropTypes from 'prop-types';

import {
    getMealTypesForDate,
    getIngredientTagsForMeals,
    getPrimaryMeal,
    getTagsForMeals,
    mealSortCompare,
    enrichMealsForSorting
} from '../../utils/Meals';
import { compareNutrientsToEnvelope, getNutrientsForMeals } from '../../utils/Nutrition';
import AddMissingMeal from './Feed/AddMissingMeal.react';
import ChangePasswordModal from './Feed/ChangePasswordModal.react';
import FeedCoachmark from './Feed/Coachmark.react';
import FeedGoals from './Goals.react';
import SpringFeed from './SpringFeed.react';
import MealCard from './Feed/MealCard.react';
import MealStore from '../../stores/MealStore';
import MealActions from '../../actions/MealActions';
import PlanActions from '../../actions/PlanActions';
import { useMealTypeNavigation, useMealTypeValue, useMealTypeActions } from '@components/MealType/MealTypeProvider.react';

import './Feed.scss';

const FEED_SIZE_PAST = 7;
const FEED_SIZE_FUTURE = 7;

// Context provider class for MealCard to access legacy context
class MealCardContextProvider extends Component {
    static childContextTypes = {
        router: PropTypes.object,
        location: PropTypes.object,
        user: PropTypes.object,
        preferences: PropTypes.object,
        family: PropTypes.array,
        meals: PropTypes.array,
        orders: PropTypes.array,
        plans: PropTypes.array,
        groceries: PropTypes.array,
        recipes: PropTypes.object,
        foods: PropTypes.object,
        brands: PropTypes.object,
        synced: PropTypes.bool,
        loaded: PropTypes.bool,

        // Actions from MealActionsWrapper
        onModifyMeals: PropTypes.func,
        onRemoveMeals: PropTypes.func,
        startAddMeal: PropTypes.func,
        startReplaceMeal: PropTypes.func,
        startRescheduleMeal: PropTypes.func,
        showMealDetails: PropTypes.func,
        syncMealsToGroceries: PropTypes.func,
        removeMealsFromGroceries: PropTypes.func,
        autopopulate: PropTypes.func,

        // Additional functions needed by MealCard
        createMealFromRecipe: PropTypes.func,
        createMealsFromCombo: PropTypes.func,
        getLeftoverDateOverlaps: PropTypes.func,
        getDefaultAddSwapSettings: PropTypes.func,
        changeCurrentMealType: PropTypes.func,
        addSwapContext: PropTypes.object,

        // Platform info
        isMobile: PropTypes.bool,
        isAndroid: PropTypes.bool,
    };

    getChildContext() {
        const { feedProps } = this.props;
        return {
            router: feedProps.router,
            location: feedProps.location,
            user: feedProps.user,
            preferences: feedProps.preferences,
            family: feedProps.family,
            meals: feedProps.meals,
            orders: feedProps.orders,
            plans: feedProps.plans,
            groceries: feedProps.groceries,
            recipes: feedProps.recipes,
            foods: feedProps.foods,
            brands: feedProps.brands,
            synced: feedProps.synced,
            loaded: feedProps.loaded,

            // Actions from MealActionsWrapper
            onModifyMeals: feedProps.onModifyMeals,
            onRemoveMeals: feedProps.onRemoveMeals,
            startAddMeal: feedProps.startAddMeal,
            startReplaceMeal: feedProps.startReplaceMeal,
            startRescheduleMeal: feedProps.startRescheduleMeal,
            showMealDetails: feedProps.showMealDetails,
            syncMealsToGroceries: feedProps.syncMealsToGroceries,
            removeMealsFromGroceries: feedProps.removeMealsFromGroceries,
            autopopulate: feedProps.autopopulate,

            // Additional functions needed by MealCard
            createMealFromRecipe: feedProps.createMealFromRecipe,
            createMealsFromCombo: feedProps.createMealsFromCombo,
            getLeftoverDateOverlaps: feedProps.getLeftoverDateOverlaps,
            getDefaultAddSwapSettings: feedProps.getDefaultAddSwapSettings,
            changeCurrentMealType: feedProps.changeCurrentMealType,
            addSwapContext: feedProps.addSwapContext,

            // Platform info
            isMobile: feedProps.isMobile,
            isAndroid: feedProps.isAndroid,
        };
    }

    render() {
        const { feedProps, item, ...mealCardProps } = this.props;
        return (
            <MealCard
                item={item}
                ref={(el) => (item.element = el)}
                {...mealCardProps}
            />
        );
    }
}

// Use legacy context via contextTypes instead of DashboardContext
const Feed = forwardRef((props, ref) => {
    const {
        currentMealType,
        activeMealType,
        isNavigating,
        shouldNavigateToToday,
        navigateToMealType,
        clearManualSelection,
        clearNavigateToToday,
        onToggleView,
        // Context props passed from FeedWithContext
        user,
        preferences,
        meals,
        recipes,
        foods,
        plans,
        synced,
        loaded,
        router,
        location,
        viewportWidth,
        isCordova
    } = props;

    // Access Dashboard context via legacy context (passed via getChildContext)
    // Note: This will be available through the wrapper component FeedWithMealTypes

    // Get router location
    const routerLocation = router?.location || location;

    // Initial State
    const getInitialState = useCallback(() => {
        const defaultActiveDate = user?.start_date && moment().isSameOrBefore(user.start_date, 'day')
            ? moment(user.start_date)
            : moment();
        const activeDate = routerLocation?.query?.date ? moment(routerLocation.query.date) : defaultActiveDate;

        const startDate = moment(activeDate).subtract(FEED_SIZE_PAST, 'days'),
            endDate = moment(activeDate).add(FEED_SIZE_FUTURE, 'days');

        return {
            startDate,
            endDate,
            activeDate,
            thumped: null,
            activeFlipped: false,
            showSplash: false,
            isCoachmarkVisible: false
        };
    }, [user, router, location, activeMealType, currentMealType]);

    // State hooks
    const [state, setState] = useState(getInitialState);
    const [activeItem, setActiveItem] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Track previous activeDate to detect date changes vs meal-type-only changes
    const prevActiveDateRef = useRef(null);

    // Sync activeDate when user.start_date is in the future (runs once per start_date value)
    const syncedStartDateRef = useRef(null);
    useEffect(() => {
        const currentStartDate = user?.start_date;
        if (!currentStartDate) return;

        // Only sync once per start_date value
        if (syncedStartDateRef.current === currentStartDate) return;

        const startDateMoment = moment(currentStartDate);
        const today = moment();

        // Only process if start_date is in the future
        if (!startDateMoment.isAfter(today, 'day')) return;

        // Mark as synced
        syncedStartDateRef.current = currentStartDate;

        // Recenter the feed on the user's start_date
        const newActiveDate = startDateMoment;
        const newStartDate = moment(newActiveDate).subtract(FEED_SIZE_PAST, 'days');
        const newEndDate = moment(newActiveDate).add(FEED_SIZE_FUTURE, 'days');

        setState(prev => ({
            ...prev,
            activeDate: newActiveDate,
            startDate: newStartDate,
            endDate: newEndDate,
        }));
    }, [user?.start_date]);

    // Refs
    const mealStoreListenerRef = useRef(null);
    const handleResizeRef = useRef(null);

    // Auxiliar function getMappings - momoized
    const getMappings = useCallback((startDate, endDate) => {
        const mappings = {};

        for (let curDate = moment(startDate); curDate.isSameOrBefore(endDate, 'day'); curDate.add(1, 'day')) {
            const dateKey = curDate.format('YYYY-MM-DD');
            mappings[dateKey] = getMealTypesForDate(curDate, plans, preferences?.meal_types);
        }

        return mappings;
    }, [plans, preferences]);

    // Transform violations from backend format to ViolationsPopup format
    const transformViolations = useCallback((backendViolations) => {
        if (!backendViolations || !Array.isArray(backendViolations)) {
            return null;
        }

        return backendViolations.map(violation => ({
            nutrNo: violation.nutrient_number,
            value: violation.actual_amount,
            min: violation.required_minimum || null,
            max: violation.allowed_maximum || null,
        }));
    }, []);

    // Generated feed items function
    const items = useMemo(() => {
        if (!user) return [];

        let curDate;
        let items = [];
        const now = moment();

        const mealTypesForAllDates = getMappings(state.startDate, state.endDate);

        const mealsIndexByDate = {};
        meals?.forEach((meal) => {
            mealsIndexByDate[meal.date] = mealsIndexByDate[meal.date] || [];
            mealsIndexByDate[meal.date].push(meal);
        });

        const allContent = { ...recipes, ...foods };
        const { avoidances: userAvoidances = [] } = preferences || {};

        for (curDate = moment(state.startDate); curDate.isSameOrBefore(state.endDate, 'day'); curDate.add(1, 'day')) {
            let dayMode = 'future';
            let dateKey = curDate.format('YYYY-MM-DD');
            let mealTypesToAdd = mealTypesForAllDates[dateKey];

            if (!mealTypesToAdd?.length) continue;

            const daysMeals = mealsIndexByDate[dateKey] || [];

            if (now.isSame(curDate, 'day')) {
                dayMode = 'current';
            } else if (now.isAfter(curDate, 'day')) {
                dayMode = 'past';
            }

            mealTypesToAdd.forEach((mealType, index) => {
                let mode = dayMode;
                const dayMeal = daysMeals.find((meal) => meal.meal == mealType.name);
                const dayMealParticipants = dayMeal?.participants;

                if (mode === 'current' && index < mealTypesToAdd.findIndex((mt) => mt.name == currentMealType)) {
                    mode = 'past';
                } else if (mode === 'current' && index > mealTypesToAdd.findIndex((mt) => mt.name == currentMealType)) {
                    mode = 'future';
                }


                const itemMeals = daysMeals.filter((meal) => meal.meal === mealType.name);

                // Calculate tags and avoidances
                let ingredientTags = [];
                let mealTags = [];
                let avoidances = [];
                let isGrabAndGo = false;
                let primaryContent = null;
                let isRestaurantDish = false;
                let isBrandProduct = false;

                if (itemMeals.length && synced) {
                    // Get ingredient tags
                    ingredientTags = getIngredientTagsForMeals(itemMeals, allContent);

                    // Get meal tags
                    const nonDeletedMeals = itemMeals.filter((meal) => !meal.deleted);
                    mealTags = getTagsForMeals(nonDeletedMeals, allContent);

                    // Calculate avoidances (user's dietary restrictions found in this meal)
                    avoidances = ingredientTags.filter((tag) => userAvoidances?.includes(tag));

                    // Check if Grab & Go
                    isGrabAndGo = mealTags.includes('Grab & Go') || ingredientTags.includes('Grab & Go');

                    // Get primary meal content
                    const primaryMealData = getPrimaryMeal(itemMeals, allContent, allContent);
                    primaryContent = primaryMealData.content;

                    // Check if restaurant dish or brand product
                    if (primaryContent) {
                        isRestaurantDish = !!primaryContent.merchant;
                        isBrandProduct = !!primaryContent.brand;
                    }
                }

                // EAT-2629: Get violations from store (lazy-loaded)
                const dateViolations = MealStore.getViolationsForDate(curDate);
                let violations = null;

                if (dateViolations && dateViolations.per_meal_violations && dateViolations.per_meal_violations[mealType.name]) {
                    violations = transformViolations(dateViolations.per_meal_violations[mealType.name]);
                }

                items.push({
                    date: moment(curDate),
                    mealType: mealType.name,
                    mode,
                    meal: dayMeal,
                    meals: itemMeals, // MealCard expects meals array
                    violations,
                    mealTypeMeta: mealType,
                    // Restored properties
                    ingredientTags,
                    mealTags,
                    avoidances,
                    isGrabAndGo,
                    primaryContent,
                    isRestaurantDish,
                    isBrandProduct,
                });
            });
        }

        return items;
    }, [user, meals, recipes, foods, plans, preferences, currentMealType, synced, getMappings, state.startDate, state.endDate, state.lastUpdated, transformViolations]);

    useEffect(() => {
        const today = moment();
        const targetDate = state.activeDate || today;
        const isTargetDateFuture = targetDate.isAfter(today, 'day');
        const isDateChange = !prevActiveDateRef.current || !prevActiveDateRef.current.isSame(targetDate, 'day');
        prevActiveDateRef.current = moment(targetDate);

        // For future dates on initial landing (date changed), force first-meal selection via fallback.
        // For same-date navigation (arrows), respect activeMealType match.
        let newActiveItem = (isTargetDateFuture && isDateChange)
            ? null
            : items.find(
                (item) => moment(item.date).isSame(state.activeDate, 'day') && item.mealType === activeMealType
            );

        // Fallback: if the activeItem is not found, use intelligent logic
        if (!newActiveItem && items.length > 0) {

            // 1. Priority: items for the target date
            const targetDateItems = items.filter(item =>
                moment(item.date).isSame(targetDate, 'day')
            );

            if (targetDateItems.length > 0) {
                if (isTargetDateFuture) {
                    // For future dates: use FIRST meal of the day (items are sorted by begin time)
                    // Don't use currentMealType which is based on current time
                    newActiveItem = targetDateItems[0];
                } else {
                    // For today: prefer currentMealType, otherwise first from today
                    const todayCurrentItem = targetDateItems.find(item => item.mealType === currentMealType);
                    newActiveItem = todayCurrentItem || targetDateItems[0];
                }

                // Keep activeDate as targetDate
                setState(prev => ({
                    ...prev,
                    activeDate: targetDate
                }));

                // Navigate to the meal type of the chosen item
                if (navigateToMealType && newActiveItem.mealType !== activeMealType) {
                    navigateToMealType(newActiveItem.mealType);
                }
            } else {
                // 2. Fallback: first available item (sorted by date and meal time)
                newActiveItem = items[0];

                // Update activeDate to the date of the chosen item
                setState(prev => ({
                    ...prev,
                    activeDate: newActiveItem.date
                }));

                // Navigate to the meal type of the chosen item
                if (navigateToMealType && newActiveItem.mealType !== activeMealType) {
                    navigateToMealType(newActiveItem.mealType);
                }
            }
        }

        setActiveItem(newActiveItem);
    }, [items, state.activeDate, activeMealType, currentMealType, navigateToMealType]);

    // Scroll to meal function
    const scrollToMeal = useCallback((activeDate, activeMeal, animate = true, callback = null) => {
        if (!process.browser) {
            return;
        }

        const targetItem = items.find(
            (item) => activeDate.isSame(item.date, 'day') && item.mealType === activeMeal
        );

        if (!targetItem) {
            return;
        }

        // Update date
        setState(prev => ({
            ...prev,
            activeDate
        }));

        // Use MealType V2 for meal type
        if (navigateToMealType && activeMeal !== activeMealType) {
            navigateToMealType(activeMeal);
        }

        setActiveItem(targetItem);

        if (callback) callback();
    }, [items, activeMealType, navigateToMealType]);

    // Navigation functions com throttling
    const next = useCallback((e) => {
        if (isTransitioning) {
            return;
        }

        if (!items.length) {
            return;
        }

        const currentActiveItem = items.find(
            (item) => item.mealType === activeMealType && item.date.isSame(state.activeDate, 'day')
        );

        // If no current item found, use activeItem or first item
        const currentItem = currentActiveItem || activeItem || items[0];
        const currentIndex = items.indexOf(currentItem);
        const nextIndex = currentIndex + 1;

        if (nextIndex >= items.length) {
            return; // At the end
        }

        const nextItem = items[nextIndex];

        // Set transitioning flag briefly to prevent double clicks
        setIsTransitioning(true);

        // EAT-2629: Load violations for new date if date changes
        if (!nextItem.date.isSame(state.activeDate, 'day')) {
            MealActions.loadViolationsForDateRange(nextItem.date);
        }

        // Update all states synchronously for smooth animation
        setState(prev => ({
            ...prev,
            activeDate: nextItem.date
        }));

        setActiveItem(nextItem);

        // Use MealType V2 navigation if meal type changes
        if (navigateToMealType && nextItem.mealType !== activeMealType) {
            navigateToMealType(nextItem.mealType);
        }

        // Reset transitioning flag quickly
        setTimeout(() => setIsTransitioning(false), 100);

        // Recenter if needed
        setTimeout(() => centerScrollable(), 0);
    }, [items, state.activeDate, activeItem, activeMealType, navigateToMealType, isTransitioning, centerScrollable]);

    const prev = useCallback((e) => {
        if (isTransitioning) return;
            if (!items.length) {
                return;
            }

            const currentActiveItem = items.find(
                (item) => item.mealType === activeMealType && item.date.isSame(state.activeDate, 'day')
            );

            // If no current item found, use activeItem or first item
            const currentItem = currentActiveItem || activeItem || items[0];
            const currentIndex = items.indexOf(currentItem);
            const prevIndex = currentIndex - 1;

            if (prevIndex < 0) {
                return; // At the beginning
            }

            const prevItem = items[prevIndex];

            // Set transitioning flag briefly to prevent double clicks
            setIsTransitioning(true);

            // EAT-2629: Load violations for new date if date changes
            if (!prevItem.date.isSame(state.activeDate, 'day')) {
                MealActions.loadViolationsForDateRange(prevItem.date);
            }

            // Update all states synchronously for smooth animation
            setState(prev => ({
                ...prev,
                activeDate: prevItem.date
            }));

            setActiveItem(prevItem);

            // Use MealType V2 navigation instead of local state
            if (navigateToMealType && prevItem.mealType !== activeMealType) {
                navigateToMealType(prevItem.mealType);
            }

            // Reset transitioning flag quickly
            setTimeout(() => setIsTransitioning(false), 100);

            // Recenter if needed
            setTimeout(() => centerScrollable(), 0);
    }, [items, state.activeDate, activeItem, activeMealType, navigateToMealType, isTransitioning, centerScrollable]);

    // Go to current meal
    const goToCurrentMeal = useCallback(() => {
        const today = moment();

        // Check if today is already within the current range
        const todayInRange = today.isBetween(state.startDate, state.endDate, 'day', '[]');

        if (todayInRange) {
            // Today is in range - just navigate to the current meal without recalculating range
            // Find the target item for today + currentMealType
            const targetItem = items.find(
                item => item.date.isSame(today, 'day') && item.mealType === currentMealType
            );

            if (targetItem) {
                setState(prev => ({
                    ...prev,
                    activeDate: today
                }));
                setActiveItem(targetItem);

                // Navigate to current meal type
                if (navigateToMealType) {
                    navigateToMealType(currentMealType);
                }
                return;
            }
        }

        // Today is NOT in range - recenter the range on today
        const newStartDate = moment(today).subtract(FEED_SIZE_PAST, 'days');
        const newEndDate = moment(today).add(FEED_SIZE_FUTURE, 'days');

        setState(prev => ({
            ...prev,
            startDate: newStartDate,
            endDate: newEndDate,
            activeDate: today
        }));

        // Navigate to current meal type
        if (navigateToMealType) {
            navigateToMealType(currentMealType);
        }
    }, [currentMealType, navigateToMealType, state.startDate, state.endDate, items]);

    // React to activeMealType changes (from Header dropdown)
    // Only navigate if the activeMealType is different from current activeItem
    useEffect(() => {
        if (activeMealType && items.length > 0) {
            // Only update if activeItem doesn't match activeMealType
            if (activeItem?.mealType !== activeMealType) {
                // Find the corresponding item for the current date and new meal type
                const targetItem = items.find(
                    item => item.date.isSame(state.activeDate, 'day') &&
                            item.mealType === activeMealType
                );

                if (targetItem) {
                    setActiveItem(targetItem);
                } else {
                    // If no item on current date, try to find it on any date
                    const anyDateItem = items.find(item => item.mealType === activeMealType);
                    if (anyDateItem) {
                        setState(prev => ({
                            ...prev,
                            activeDate: anyDateItem.date
                        }));
                        setActiveItem(anyDateItem);
                    }
                }
            }
        }
    }, [activeMealType, items, state.activeDate, activeItem]);

    // React to shouldNavigateToToday flag - navigate to today's date and current meal type
    useEffect(() => {
        if (shouldNavigateToToday) {
            const today = moment();

            // Use scrollToMeal to navigate to today + current meal type
            scrollToMeal(today, currentMealType, true, () => {
                // Clear the flag after navigating
                if (clearNavigateToToday) {
                    clearNavigateToToday();
                }
            });
        }
    }, [shouldNavigateToToday, currentMealType, scrollToMeal, clearNavigateToToday]);

    // Store listeners
    useEffect(() => {
        mealStoreListenerRef.current = () => {
            // Force re-render when meals change by updating a dummy state
            // Items will be recalculated via useMemo
            setState(prev => ({ ...prev, lastUpdated: Date.now() }));
        };

        MealStore.addChangeListener(mealStoreListenerRef.current);

        return () => {
            MealStore.removeChangeListener(mealStoreListenerRef.current);
        };
    }, []);

    // Ensure data is loaded for the date range when it changes
    useEffect(() => {
        if (state.startDate && state.endDate) {
            // Defer to next tick to avoid dispatch conflicts
            setTimeout(() => {
                MealActions.ensureDateRangeLoaded(state.startDate, state.endDate);
                PlanActions.ensureDateRangeLoaded(state.startDate, state.endDate);
                // EAT-2629: Load violations for active date
                MealActions.loadViolationsForDateRange(state.activeDate);
            }, 0);
        }
    }, [state.startDate, state.endDate]);

    // Resize handler
    useEffect(() => {
        handleResizeRef.current = debounce(() => {
            scrollToMeal(state.activeDate, activeMealType, false);
        }, 100);

        window.addEventListener('resize', handleResizeRef.current);
        return () => window.removeEventListener('resize', handleResizeRef.current);
    }, [scrollToMeal, state.activeDate, activeMealType]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Ignore keyboard shortcuts if user is typing in an input/textarea/select
            const target = event.target;
            const isTyping = target.tagName === 'INPUT' ||
                            target.tagName === 'TEXTAREA' ||
                            target.tagName === 'SELECT' ||
                            target.isContentEditable;

            // Ignore if inside a modal (check for common modal classes/attributes)
            const isInModal = target.closest('.el-modal-container, .modal, [role="dialog"]');

            if (isTyping || isInModal) {
                return; // Let the default behavior happen
            }

            // Press Left arrow to go to previous
            if (event.key === 'ArrowLeft') {
                prev(event);
                event.preventDefault();
                return;
            }

            // Press Right arrow to go to next
            if (event.key === 'ArrowRight') {
                next(event);
                event.preventDefault();
                return;
            }

            // Press Enter or Space to select/open active card
            if (event.key === 'Enter' || event.key === ' ') {
                if (activeItem && onClickItemImage) {
                    onClickItemImage(activeItem);
                    event.preventDefault();
                }
                return;
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [clearManualSelection, user?.is_internal, activeItem, onClickItemImage, next, prev]);

    // Reset thumped with debounce
    const resetThumped = useCallback(
        debounce(() => {
            setState(prev => ({
                ...prev,
                thumped: null
            }));
        }, 50),
        []
    );

    // Center scrollable on active item
    const centerScrollable = useCallback(() => {
        if (!process.browser || !activeItem) return;

        const activeIndex = items.indexOf(activeItem);
        if (activeIndex === -1) return;

        // Don't re-center if we don't need to (at least 6 items before AND after)
        if (activeIndex > 6 && items.length - activeIndex > 6) {
            // Just scroll visually
            const element = document.querySelector('.spring-feed-item.active');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Need to recenter - calculate new range based on activeDate
        const newStartDate = moment(state.activeDate).subtract(FEED_SIZE_PAST, 'days');
        const newEndDate = moment(state.activeDate).add(FEED_SIZE_FUTURE, 'days');

        // Only recenter if startDate actually changed
        if (state.startDate.isSame(newStartDate, 'day')) {
            return;
        }

        // Update date range (this will trigger useMemo to recalculate items)
        setState(prev => ({
            ...prev,
            startDate: newStartDate,
            endDate: newEndDate
        }));
    }, [activeItem, items, state.activeDate, state.startDate]);

    // Toggle active flipped
    const toggleActiveFlipped = useCallback(() => {
        setState(prev => ({
            ...prev,
            activeFlipped: !prev.activeFlipped
        }));
    }, []);

    // Click item image handler
    const onClickItemImage = useCallback((item) => {
        const { startAddMeal, showMealDetails } = props;

        // 1. Thump animation
        setState(prev => ({
            ...prev,
            thumped: item
        }));

        // Use debounced reset instead of setTimeout
        resetThumped();

        // 2. Check if we need to navigate or open details
        const needsDateUpdate = !item.date.isSame(state.activeDate, 'day');
        const needsMealTypeUpdate = item.mealType !== activeMealType;

        // If clicking on a different meal type or date, navigate to it
        if (needsDateUpdate || needsMealTypeUpdate) {
            if (needsDateUpdate) {
                setState(prev => ({
                    ...prev,
                    activeDate: item.date
                }));
            }

            if (needsMealTypeUpdate && navigateToMealType) {
                navigateToMealType(item.mealType);
            }

            setActiveItem(item);

            // Recenter if needed (same as next/prev do)
            setTimeout(() => centerScrollable(), 0);

            return;
        }

        // If clicking on the same meal type and date, open details or add meal
        const liveMeals = (item.meals || []).filter((m) => !m.deleted);

        // If no meals exist, open add meal modal
        if (!liveMeals.length) {
            const options = {};

            if (item.mode === 'past') {
                options.auto_log = true;
                options.defaultMode = 'browser';
            } else {
                options.fullBrowserParams = { defaultTypes: ['recipe'] };
            }

            if (startAddMeal) {
                return startAddMeal(item.date, item.mealType, options);
            }
        }

        // If past or logged meal, go to daily log
        if (item.mode === 'past' || (item.mode === 'current' && item.meals[0] && item.meals[0].logged_portion)) {
            if (router) {
                router.push(`/log/${item.date.format('YYYY-MM-DD')}`);
                return;
            }
        }

        // Otherwise, show meal details
        if (showMealDetails) {
            showMealDetails(item.meals);
        }
    }, [activeMealType, navigateToMealType, state.activeDate, props, router, centerScrollable]);

    // Render individual item
    const renderItem = useCallback((item, i) => {
        const { thumped, activeFlipped } = state;

        return (
            <MealCardContextProvider
                key={`${item.date.format('YYYY-MM-DD')}-${item.mealType}`}
                feedProps={props}
                item={item}
                items={items}
                currentIndex={i}
                thumped={thumped}
                activeDate={state.activeDate}
                activeMeal={activeMealType}
                activeItem={activeItem}
                activeFlipped={activeFlipped}
                currentMealType={currentMealType}
                scrollToNextCard={next}
                onClickItemImage={onClickItemImage}
                toggleActiveFlipped={toggleActiveFlipped}
            />
        );
    }, [items, state, activeItem, currentMealType, activeMealType, next, onClickItemImage, toggleActiveFlipped, props]);

    // Check if we should show coachmark on signup
    useEffect(() => {
        if (routerLocation?.query?.signup) {
            setState(prev => ({ ...prev, isCoachmarkVisible: true }));
        }
    }, [routerLocation]);

    // Render coachmark
    const renderCoachmark = useCallback(() => {
        if (!state.isCoachmarkVisible) return null;

        const closeCoachmark = () => {
            // Remove signup query param if it exists (create new object to avoid mutating prevProps)
            if (routerLocation?.query?.signup) {
                const { pathname, query, hash } = routerLocation;
                const { signup, ...rest } = query;
                router.push({ pathname, query: rest, hash });
            }

            setState(prev => ({ ...prev, isCoachmarkVisible: false }));
        };

        return (
            <FeedCoachmark
                closeModal={closeCoachmark}
                user={user}
            />
        );
    }, [state.isCoachmarkVisible, routerLocation, router, user]);

    // Render change password modal
    const renderChangePassword = useCallback(() => {
        if (!routerLocation?.query?.['change-password']) {
            return null;
        }

        const closeChangePassword = () => {
            const { pathname, query, hash } = routerLocation;
            const { 'change-password': _, ...rest } = query;
            router.push({ pathname, query: rest, hash });
        };

        return <ChangePasswordModal closeModal={closeChangePassword} />;
    }, [routerLocation, router]);

    // Ref for focusFirstMeal flag
    const focusFirstMealRef = useRef(true);

    // Begin autopopulate - mark items as populating
    const beginAutopopulate = useCallback((startDate) => {
        const endDate = moment(startDate).add(user?.shopping_freq || 7, 'day');

        // Mark items that are being populated
        const updatedItems = items.map(item => {
            if (startDate.isSameOrBefore(item.date, 'day') &&
                endDate.isSameOrAfter(item.date, 'day')) {
                return { ...item, populating: true };
            }
            return item;
        });

        // Force re-render with populating state
        setActiveItem(prev => prev); // Trigger re-render
    }, [items, user?.shopping_freq]);

    // Complete autopopulate - handle response and scroll to first meal
    const completeAutopopulate = useCallback((response, replace = false, splash = false) => {
        // Clear populating flags
        const clearedItems = items.map(item => {
            const newItem = { ...item };
            delete newItem.populating;
            return newItem;
        });

        // Check if should show coachmark
        const shouldShowCoachmark = routerLocation?.query?.signup ? true : false;
        if (shouldShowCoachmark) {
            setState(prev => ({ ...prev, isCoachmarkVisible: true }));
        }

        // If we have response with meals, find first and scroll to it
        if (response?.meals?.length > 0) {
            const enrichedMeals = enrichMealsForSorting([...response.meals], recipes, {}, foods);
            const sortedMeals = enrichedMeals.sort(mealSortCompare);
            const firstMeal = sortedMeals[0];

            // Scroll to first meal if splash mode and focusFirstMeal is true
            if (focusFirstMealRef.current && firstMeal && splash) {
                const mealDate = moment(firstMeal.date);
                const mealType = firstMeal.meal;

                // Update state and scroll
                setState(prev => ({
                    ...prev,
                    activeDate: mealDate
                }));

                // Navigate to the meal type
                if (navigateToMealType) {
                    navigateToMealType(mealType);
                }

                // Scroll to the meal
                setTimeout(() => {
                    scrollToMeal(mealDate, mealType, true);
                }, 100);

                focusFirstMealRef.current = false;
            }
        }

        // Force re-render
        setActiveItem(prev => prev);
    }, [items, routerLocation, navigateToMealType, scrollToMeal, recipes]);

    // Expose methods via ref for Dashboard compatibility
    useImperativeHandle(ref, () => ({
        scrollToMeal,
        setActiveMeal: scrollToMeal, // Alias for compatibility
        goToCurrentMeal,
        beginAutopopulate,
        completeAutopopulate
    }), [scrollToMeal, goToCurrentMeal, beginAutopopulate, completeAutopopulate]);

    // Main render
    const timeMode = (() => {
        const now = moment();
        if (now.isBefore(state.activeDate, 'day')) return 'future';
        if (now.isAfter(state.activeDate, 'day')) return 'past';
        return 'present';
    })();

    const nextPrevClasses = ['next-prev'];
    let isSafariMobile = false;

    if (viewportWidth !== undefined && viewportWidth <= 414 && !isCordova && platform.name === 'Safari') {
        nextPrevClasses.push('safari-fix');
        isSafariMobile = true;
    }

    return (
        <div className="meal-feed meal-feed-bg" data-bg-mode={timeMode}>
            <div className="bg-container">
                <div className="swipable-area">
                    <AddMissingMeal activeDate={state.activeDate} />

                    <SpringFeed
                        items={items}
                        activeDate={state.activeDate}
                        activeMeal={activeMealType || currentMealType}
                        activeItem={activeItem}
                        startDate={state.startDate}
                        endDate={state.endDate}
                        currentMealType={currentMealType}
                        nextCard={next}
                        prevCard={prev}
                        isSafariMobile={isSafariMobile}
                        onClickItemImage={onClickItemImage}
                        renderItem={renderItem}
                        thumped={state.thumped}
                        activeFlipped={state.activeFlipped}
                        scrollToNextCard={next}
                        toggleActiveFlipped={toggleActiveFlipped}
                    />

                    <footer className={classNames(nextPrevClasses)}>
                        <button className="prev-card-btn" onClick={prev}>
                            <i className="icon-chevron-left" />
                        </button>
                        <button className="next-card-btn" onClick={next}>
                            <i className="icon-chevron-right" />
                        </button>
                    </footer>
                </div>

                <div className="current-meal-btn-container">
                    <button
                        className="current-meal-btn"
                        data-visible={!(timeMode === 'present' && activeMealType === currentMealType)}
                        onClick={goToCurrentMeal}
                    >
                        go to current meal
                    </button>
                </div>

                <FeedGoals />
                {renderCoachmark()}
                {renderChangePassword()}
            </div>
        </div>
    );
});

Feed.propTypes = {
    currentMealType: PropTypes.string,
    activeMealType: PropTypes.string,
    isNavigating: PropTypes.bool,
    shouldNavigateToToday: PropTypes.bool,
    navigateToMealType: PropTypes.func,
    clearManualSelection: PropTypes.func,
    clearNavigateToToday: PropTypes.func,
    onToggleView: PropTypes.func,
};

Feed.displayName = 'Feed';

// Wrapper class component to access legacy context
class FeedWithContext extends Component {
    static contextTypes = {
        // Router for navigation
        router: PropTypes.object,
        location: PropTypes.object,

        // User assets
        user: PropTypes.object,
        preferences: PropTypes.object,
        family: PropTypes.array,

        // Meal assets
        meals: PropTypes.array,
        orders: PropTypes.array,
        plans: PropTypes.array,
        groceries: PropTypes.array,
        recipes: PropTypes.object,
        foods: PropTypes.object,
        brands: PropTypes.object,
        synced: PropTypes.bool,
        loaded: PropTypes.bool,

        // Actions from MealActionsWrapper
        onModifyMeals: PropTypes.func,
        onRemoveMeals: PropTypes.func,
        startAddMeal: PropTypes.func,
        startReplaceMeal: PropTypes.func,
        startRescheduleMeal: PropTypes.func,
        showMealDetails: PropTypes.func,
        syncMealsToGroceries: PropTypes.func,
        removeMealsFromGroceries: PropTypes.func,
        autopopulate: PropTypes.func,

        // Additional functions needed by MealCard
        createMealFromRecipe: PropTypes.func,
        createMealsFromCombo: PropTypes.func,
        getLeftoverDateOverlaps: PropTypes.func,
        getDefaultAddSwapSettings: PropTypes.func,
        changeCurrentMealType: PropTypes.func,
        addSwapContext: PropTypes.object,

        // Platform info
        viewportWidth: PropTypes.number,
        isCordova: PropTypes.bool,
        isMobile: PropTypes.bool,
        isAndroid: PropTypes.bool,
    };

    render() {
        return (
            <Feed
                {...this.props}
                {...this.context} // Pass all context as props
            />
        );
    }
}

// Wrapper that connects MealType V2 system
const FeedWithMealTypes = forwardRef((props, ref) => {
    const { activeMealType, isNavigating, navigateToMealType, shouldNavigateToToday, clearNavigateToToday, syncNavigationToCurrent } = useMealTypeNavigation();
    const { currentMealType } = useMealTypeValue();
    const { clearManualSelection } = useMealTypeActions();

    return (
        <FeedWithContext
            {...props}
            ref={ref}
            currentMealType={currentMealType}
            activeMealType={activeMealType}
            isNavigating={isNavigating}
            shouldNavigateToToday={shouldNavigateToToday}
            navigateToMealType={navigateToMealType}
            clearManualSelection={clearManualSelection}
            clearNavigateToToday={clearNavigateToToday}
        />
    );
});

FeedWithMealTypes.displayName = 'FeedWithMealTypes';

export default FeedWithMealTypes;